import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import {
  type ProjectDetail,
  type ProjectFinance,
  type ProjectItem,
  type ProjectSubproject,
  type ProjectSubprojectCreatePayload,
  type Uuid,
} from '../types/projects';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  notFound: 'No encontramos información para el proyecto solicitado.',
  unknown: 'No pudimos conectar con el servicio de proyectos. Inténtalo de nuevo en unos segundos.',
} as const;

export function useProjectsApi() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de proyectos');
      }

      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/projects${config.url ?? ''}`,
        });

        return response.data;
      } catch (error) {
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401) {
            throw new Error(ERROR_MESSAGES.unauthorized);
          }
          if (status === 404) {
            throw new Error(ERROR_MESSAGES.notFound);
          }
        }
        throw new Error(ERROR_MESSAGES.unknown);
      }
    },
    [token],
  );

  const fetchProjectDetail = useCallback(
    (projectId: Uuid) =>
      request<ProjectDetail>({
        method: 'GET',
        url: `/${projectId}/detail`,
      }),
    [request],
  );

  const fetchProjectSubprojects = useCallback(
    (projectId: Uuid) =>
      request<ProjectSubproject[]>({
        method: 'GET',
        url: `/${projectId}/subprojects`,
      }),
    [request],
  );

  const fetchProjectItems = useCallback(
    (projectId: Uuid) =>
      request<ProjectItem[]>({
        method: 'GET',
        url: `/${projectId}/items`,
      }),
    [request],
  );

  const fetchProjectFinances = useCallback(
    (projectId: Uuid) =>
      request<ProjectFinance>({
        method: 'GET',
        url: `/${projectId}/finances`,
      }),
    [request],
  );

  const createProjectSubproject = useCallback(
    (projectId: Uuid, payload: ProjectSubprojectCreatePayload) =>
      request<ProjectSubproject>({
        method: 'POST',
        url: `/${projectId}/subprojects`,
        data: payload,
      }),
    [request],
  );

  return {
    fetchProjectDetail,
    fetchProjectSubprojects,
    fetchProjectItems,
    fetchProjectFinances,
    createProjectSubproject,
  };
}
